/** Phase 14 Wave 3 — Module E — Admin Payments page. */
import { useCallback, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import AdminDataTable from "@/components/admin/AdminDataTable";
import { useFmt } from "@/lib/useFmt";
import { DollarSign, Loader2, Undo2 } from "lucide-react";

const STATUS_BADGES = {
  paid: "bg-emerald-50 text-emerald-700",
  pending: "bg-amber-50 text-amber-700",
  failed: "bg-red-50 text-red-700",
  refunded: "bg-slate-100 text-slate-500",
};

function RefundDialog({ open, onOpenChange, payment, onDone }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/payments/${payment.id}/refund`, { reason: reason.trim() });
      toast.success("Marked as refunded");
      onDone?.();
      onOpenChange(false);
      setReason("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refund failed");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="payment-refund">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Undo2 className="h-4 w-4 text-amber-600" /> Refund payment</DialogTitle>
          <DialogDescription>Marks the DB row as refunded. Actual Stripe refund is deferred to Phase 8d.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Label className="text-xs">Reason (optional)</Label>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="payment-refund-reason" className="mt-1 text-sm" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="payment-refund-submit" className="bg-amber-600 text-white hover:bg-amber-700">
            {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null} Refund
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPayments() {
  const fmt = useFmt();
  const [refresh, setRefresh] = useState(0);
  const [refunding, setRefunding] = useState(null);
  const fetchPage = useCallback(async ({ skip, limit, q }) => {
    const page = Math.floor(skip / limit);
    const { data } = await api.get("/admin/payments", { params: { page, limit, q } });
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);
  const markPaid = async (p) => {
    try {
      await api.patch(`/admin/payments/${p.id}`, { status: "paid" });
      toast.success("Marked as paid");
      setRefresh((n) => n + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const columns = [
    { key: "id", label: "Payment", render: (r) => (
      <div>
        <div className="flex items-center gap-1.5"><DollarSign className="h-3 w-3 text-slate-400" />
          <span className="font-mono text-[10px] text-slate-500">{r.id?.slice(0, 8)}</span></div>
        <div className="text-xs text-slate-500">Tenant {r.tenant_id?.slice(0, 8) || "—"}</div>
      </div>
    )},
    { key: "amount", label: "Amount", align: "right", render: (r) => fmt.money(r.amount, { cents: true }) },
    { key: "due_date", label: "Due", render: (r) => (r.due_date || "").slice(0, 10) || "—" },
    { key: "status", label: "Status", render: (r) => (
      <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_BADGES[r.status] || "bg-slate-100"}`}>{r.status}</span>
    )},
  ];
  return (
    <div className="space-y-6">
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-indigo-600">Module E</p>
        <h1 className="mt-1 text-2xl font-bold tracking-tight text-slate-900">Payments</h1>
        <p className="mt-1 text-sm text-slate-500">All rent payments across the platform. Mark paid or refunded.</p>
      </div>
      <AdminDataTable
        testid="admin-payments"
        columns={columns}
        fetchPage={fetchPage}
        searchPlaceholder="Search by tenant email or amount…"
        rowActions={(row) => {
          const actions = [];
          if (row.status !== "paid" && row.status !== "refunded") actions.push({ label: "Mark paid", variant: "primary", onClick: markPaid });
          if (row.status !== "refunded") actions.push({ label: "Refund", variant: "danger", onClick: setRefunding });
          return actions;
        }}
      />
      <RefundDialog open={!!refunding} onOpenChange={(v) => !v && setRefunding(null)} payment={refunding} onDone={() => setRefresh((n) => n + 1)} />
    </div>
  );
}
